import {
  INTEGRATION_PROFILE_STATUS,
  INTEGRATION_TYPE_DISPLAY,
  type IntegrationTypeId,
} from '@steerfi/shared';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import type React from 'react';
import type { IntegrationProfileApiRow } from '@/api/integration-profiles';
import { StatusChip, type StatusChipVariant, SupplierStatus } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import { formatProviderDate } from '@/schemas/provider-form-schema';
import { type ColumnConfig, DataTable, type DataTableResponse } from './data-table';

const IP_STATUS_LABEL: Record<number, string> = {
  [INTEGRATION_PROFILE_STATUS.ACTIVE]: 'Active',
  [INTEGRATION_PROFILE_STATUS.INACTIVE]: 'Inactive',
  [INTEGRATION_PROFILE_STATUS.PENDING]: 'Pending',
};

const IP_STATUS_VARIANT: Record<number, StatusChipVariant> = {
  [INTEGRATION_PROFILE_STATUS.ACTIVE]: 'default',
  [INTEGRATION_PROFILE_STATUS.INACTIVE]: 'destructive',
  [INTEGRATION_PROFILE_STATUS.PENDING]: 'secondary',
};

interface IntegrationProfilesTableProps {
  profiles: IntegrationProfileApiRow[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number } | undefined;
  isLoading: boolean;
  error: string | null;
  onEdit: (profile: IntegrationProfileApiRow) => void;
  onDelete: (profile: IntegrationProfileApiRow) => void;
  onSortChange?: (sortBy: string, order: 'asc' | 'desc') => void;
  onPageChange?: (page: number) => void;
  headerActions?: React.ReactNode;
}

export const IntegrationProfilesTable: React.FC<IntegrationProfilesTableProps> = ({
  profiles,
  pagination,
  isLoading,
  error,
  onEdit,
  onDelete,
  onSortChange,
  onPageChange,
  headerActions,
}) => {
  const columns: ColumnConfig<IntegrationProfileApiRow>[] = [
    {
      key: 'profile_name',
      label: 'Name',
      sortable: true,
      render: (row) => <span className="font-medium text-sm">{row.profileName}</span>,
    },
    {
      key: 'integration_type_id',
      label: 'Type',
      sortable: true,
      render: (row) => (
        <span className="text-sm">
          {INTEGRATION_TYPE_DISPLAY[row.integrationTypeId as IntegrationTypeId] ??
            String(row.integrationTypeId)}
        </span>
      ),
    },
    {
      key: 'environment',
      label: 'Environment',
      sortable: true,
      render: (row) => <span className="text-sm font-mono">{row.environment}</span>,
    },
    {
      key: 'statusId',
      label: 'Status',
      sortable: false,
      render: (row) => (
        <StatusChip
          status={SupplierStatus.ACTIVE}
          labelOverride={IP_STATUS_LABEL[row.statusId] ?? String(row.statusId)}
          variant={IP_STATUS_VARIANT[row.statusId] ?? 'outline'}
        />
      ),
    },
    {
      key: 'updated_at',
      label: 'Updated',
      sortable: true,
      render: (row) => (
        <span className="text-sm text-muted-foreground">{formatProviderDate(row.updated_at)}</span>
      ),
    },
    {
      key: '_actions',
      label: '',
      sortable: false,
      align: 'right',
      render: (row) => (
        <div className="flex items-center gap-1 justify-end">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
            title="Edit integration profile"
            aria-label={`Edit integration profile ${row.profileName}`}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(row);
            }}
          >
            <IconEdit size={14} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-destructive"
            title="Delete integration profile"
            aria-label={`Delete integration profile ${row.profileName}`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row);
            }}
          >
            <IconTrash size={14} />
          </Button>
        </div>
      ),
    },
  ];

  const tableData: DataTableResponse<IntegrationProfileApiRow> = {
    data: profiles,
    ...(pagination ? { pagination } : {}),
  };

  return (
    <DataTable
      data={tableData}
      columns={columns}
      caption="Integration profiles"
      rowHeaderColumnKey="profile_name"
      headerActions={headerActions}
      isLoading={isLoading}
      error={error}
      {...(onSortChange && { onSortChange })}
      {...(onPageChange && { onPageChange })}
      emptyMessage="No integration profiles found"
      loadingMessage="Loading integration profiles..."
      defaultSortKey="profile_name"
      defaultSortOrder="asc"
      compact
    />
  );
};
